// native
import { Component, OnInit, EventEmitter, Output, Input, OnChanges, SimpleChanges } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { finalize } from 'rxjs/operators';

// addon
import { TranslateService } from '@ngx-translate/core';

// service
import { DevicesService } from 'app/core/services/devices.service';
import { OfficesService } from 'app/core/services/offices.service';
import { ErrorService } from 'app/core/services/error.service';
import { AuthService } from 'app/core/services/auth.service';
import { WorkItemService } from 'app/core/services/work-item.service';

// models
import { CenterContainerTitle, Device, DevicePool, Office, PaginatedItems } from '../../models';

// constants
import { DEVICE_TYPE } from '../../constants';

@Component({
    selector: 'app-device-form',
    templateUrl: './device-form.component.html',
    standalone: false
})
export class DeviceFormComponent implements OnInit, OnChanges {
  @Input() device: Device;
  @Input() action: string;
  // When true, the form renders without its own center-container so it can be
  // embedded inside another container (e.g. the home management wizard).
  @Input() embedded: boolean = false;
  // When set, the device type is fixed to this value and its dropdown is hidden.
  @Input() lockedDeviceType: string = null;
  // Optional initial values used to pre-fill a brand new (not-yet-created) form,
  // e.g. when an embedding wizard restores previously entered values. Unlike
  // [device], this does not switch the form into edit mode.
  @Input() initialValue: Device = null;

  @Output() close: EventEmitter<Device> = new EventEmitter<Device>();
  // Emits the form validity so an embedding parent (e.g. the wizard) can gate
  // its own Next button and trigger submission via submit().
  @Output() validChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  public form: UntypedFormGroup;
  offices: Office[] = [];

  isNew: boolean;
  deviceTypes = [];

  devicePools: DevicePool[] = [];

  title: CenterContainerTitle = {};
  isLoading: boolean = false;

  constructor(
    public formBuilder: UntypedFormBuilder,
    public devicesService: DevicesService,
    public officesService: OfficesService,
    private translateService: TranslateService,
    private errorService: ErrorService,
    public authService: AuthService,
    private workItemService: WorkItemService,
  ) { }

  ngOnInit() {
    this.isNew = !this.device;
    this.deviceTypes = this.devicesService.getDeviceTypesForTestCreation();

    this.translateService.get('device').subscribe(() => this.setTranslations());

    this.isLoading = true;
    this.officesService.getAll().pipe(
      finalize(() => this.isLoading = false)
    ).subscribe((offices: PaginatedItems<Office>) => {
      this.offices = offices.results;
      this.initializeForm();
    }, error => this.errorService.handleError(error));

    if (this.authService.isPacsIntegrationUser)
      this.workItemService.getConfig().subscribe(config => {
        this.devicePools = config.device_pools;
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes && changes['action']?.currentValue)
      this.title.thin = changes['action']?.currentValue;
  }

  private initializeForm() {
    const { name, offices, device_type, pacs_integration_device_pool } =
      this.device || this.initialValue || ({} as Device);

    const initialDeviceType = this.lockedDeviceType
      ? this.lockedDeviceType
      : (this.isNew ? DEVICE_TYPE.OFFICE : device_type);

    this.form = this.formBuilder.group({
      device_type: [{ value: initialDeviceType, disabled: !this.isNew }, Validators.required],
      name: [name, Validators.required],
      offices: [offices, Validators.required],
      pacs_integration_device_pool: [pacs_integration_device_pool?.id]
    });

    this.form.statusChanges.subscribe(() => this.validChange.emit(this.form.valid));
    this.validChange.emit(this.form.valid);
  }

  // Allows an embedding parent to trigger submission (e.g. from its Next button)
  // instead of relying on this form's own submit button.
  submit(): void {
    this.onSubmit(this.form);
  }

  // Returns the server-ready device payload from the current form values without
  // submitting it. Used by embedding parents (e.g. the home management wizard)
  // that defer device creation until a later step.
  buildBody(): Device {
    const { name, offices, device_type, pacs_integration_device_pool } = this.form.value;
    return {
      name,
      offices: this.authService ? offices.filter(office => this.offices.map(o => o.id).includes(office)) : offices,
      device_type,
      pacs_integration_device_pool
    };
  }

  private setTranslations() {
    this.title.bold = this.translateService.instant('device');
  }

  onSubmit(form: UntypedFormGroup) {
    if (!form.valid)
      return;

    const body = this.buildBody();

    const request = this.device?.id ? this.devicesService.update(this.device.id, body) : this.devicesService.create(body);

    this.isLoading = true;
    request.pipe(
      finalize(() => this.isLoading = false)
    ).subscribe(
      response => this.close.emit(response),
      error => this.errorService.handleDeviceError(error)
    );
  }
}
